import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useAuth } from './hooks/useAuth';
import { zodResolver } from '@hookform/resolvers/zod';
import { signUpSchema } from './validationSchema';
import type { SignUpFormData } from './types/auth';
import { checkUsernameAvailable, checkEmailAvailable, checkSignupEmail } from './services/authApi';
import PublicNavigation from '../../shared/components/layout/PublicNavigation';
import Footer from '../../shared/components/layout/Footer';
import AlertMessage from '../../shared/components/ui/AlertMessage';

const SignUpPage: React.FC = () => {
  const { signup, user } = useAuth();
  const navigate = useNavigate();

  // Redirect authenticated users to portfolio page
  useEffect(() => {
    if (user) {
      navigate('/portfolio', { replace: true });
    }
  }, [user, navigate]);
  const [error, setError] = useState<string | null>(null);
  const [usernameError, setUsernameError] = useState<string | null>(null);
  const [emailError, setEmailError] = useState<React.ReactNode | null>(null);
  const [checkingUsername, setCheckingUsername] = useState(false);
  const [checkingEmail, setCheckingEmail] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    watch,
  } = useForm<SignUpFormData>({
    resolver: zodResolver(signUpSchema),
  });

  const username = watch('username');
  const email = watch('email');

  // Check username uniqueness on blur
  const handleUsernameBlur = async () => {
    if (!username || errors.username) return;
    
    const normalizedUsername = username.toLowerCase().trim();
    if (normalizedUsername.length < 3) return;

    try {
      setCheckingUsername(true);
      setUsernameError(null);
      const available = await checkUsernameAvailable(normalizedUsername);
      if (!available) {
        setUsernameError('This username is already taken');
      }
    } catch (err) {
      setUsernameError('Failed to check username availability');
    } finally {
      setCheckingUsername(false);
    }
  };

  // Check email uniqueness and beta status on blur
  const handleEmailBlur = async () => {
    if (!email || errors.email) return;
    
    const normalizedEmail = email.toLowerCase().trim();
    if (!normalizedEmail.includes('@')) return; // Basic validation

    try {
      setCheckingEmail(true);
      setEmailError(null);
      const emailCheck = await checkEmailAvailable(normalizedEmail);
      if (!emailCheck.available) {
        setEmailError('This email is already registered');
      } else if (!emailCheck.on_beta_list) {
        setEmailError(
          <>
            This email is not on the approved Beta list.{' '}
            <Link to="/beta-request" className="underline font-medium">
              Request Beta access here
            </Link>
          </>
        );
      } else if (emailCheck.beta_used) {
        setEmailError('This beta email has already been used for signup');
      }
    } catch (err) {
      setEmailError('Failed to check email availability');
    } finally {
      setCheckingEmail(false);
    }
  };

  const onSubmit = async (data: SignUpFormData) => {
    try {
      setError(null);
      setUsernameError(null);
      setEmailError(null);

      // Final username check before submission
      const normalizedUsername = data.username.toLowerCase().trim();
      const usernameAvailable = await checkUsernameAvailable(normalizedUsername);
      if (!usernameAvailable) {
        setUsernameError('This username is already taken');
        return;
      }

      // Comprehensive email and beta check before submission
      const normalizedEmail = data.email.toLowerCase().trim();
      const signupCheck = await checkSignupEmail(normalizedEmail);
      
      if (!signupCheck.can_signup) {
        if (signupCheck.reason === 'email_already_used') {
          setEmailError('This email already has an account');
        } else if (signupCheck.reason === 'not_on_beta_list') {
          setEmailError(
            <>
              This email is not on the approved Beta list.{' '}
              <Link to="/beta-request" className="underline font-medium">
                Request Beta access here
              </Link>
            </>
          );
        } else if (signupCheck.reason === 'beta_email_already_used') {
          setEmailError('This beta email has already been used for signup');
        } else {
          setEmailError('Email validation failed. Please try again.');
        }
        return;
      }

      await signup({ 
        email: normalizedEmail, 
        password: data.password,
        username: normalizedUsername,
        firstName: data.firstName,
        lastName: data.lastName,
      });
      
      // Show success message instead of navigating
      setIsSubmitted(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An unexpected error occurred');
    }
  };

  if (isSubmitted) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col">
        <PublicNavigation />
        <div className="flex-grow flex items-center justify-center py-12 sm:px-6 lg:px-8">
          <div className="sm:mx-auto sm:w-full sm:max-w-md">
            <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
              <div className="text-center">
                <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100 mb-4">
                  <svg
                    className="h-6 w-6 text-green-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">Signup successful!</h3>
                <p className="text-sm text-gray-600">
                  Please <strong>check your email</strong> for a confirmation link.
                </p>
              </div>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <PublicNavigation />
      <div className="flex-grow flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
        <div className="w-full max-w-md mx-auto">
          {/* Logo and heading section - full width on all screens */}
          <div className="w-full mb-8">
            <div className="flex justify-center mb-6">
              <Link to="/">
                <img
                  src="/Vester-logo-lrg-clr.png"
                  alt="Vester"
                  className="h-20 sm:h-24 w-auto"
                />
              </Link>
            </div>
            <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
              Create your account
            </h2>
            <p className="mt-2 text-center text-sm text-gray-600">
              Or{' '}
              <Link to="/login" className="font-medium text-green-600 hover:text-green-500">
                sign in to your existing account
              </Link>
            </p>
          </div>

          {/* Form section - full width on all screens */}
          <div className="w-full">
            <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
          {error && (
            <div className="mb-4">
              <AlertMessage
                type="error"
                message={error}
                onClose={() => setError(null)}
              />
            </div>
          )}

          <form className="space-y-6" onSubmit={handleSubmit(onSubmit)}>
            <div>
              <label htmlFor="firstName" className="block text-sm font-medium text-gray-700">
                First name
              </label>
              <div className="mt-1">
                <input
                  id="firstName"
                  type="text"
                  autoComplete="given-name"
                  className={`appearance-none block w-full px-3 py-2 border ${
                    errors.firstName ? 'border-red-300' : 'border-gray-300'
                  } rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-green-500 focus:border-green-500 sm:text-sm`}
                  {...register('firstName')}
                />
                {errors.firstName && (
                  <p className="mt-2 text-sm text-red-600">{errors.firstName.message}</p>
                )}
              </div>
            </div>

            <div>
              <label htmlFor="lastName" className="block text-sm font-medium text-gray-700">
                Last name
              </label>
              <div className="mt-1">
                <input
                  id="lastName"
                  type="text"
                  autoComplete="family-name"
                  className={`appearance-none block w-full px-3 py-2 border ${
                    errors.lastName ? 'border-red-300' : 'border-gray-300'
                  } rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-green-500 focus:border-green-500 sm:text-sm`}
                  {...register('lastName')}
                />
                {errors.lastName && (
                  <p className="mt-2 text-sm text-red-600">{errors.lastName.message}</p>
                )}
              </div>
            </div>

            <div>
              <label htmlFor="username" className="block text-sm font-medium text-gray-700">
                <span className="flex items-center">
                  Username
                  <div className="ml-2 group relative">
                    <img
                      src="/icon-info-lrg.png"
                      alt="Info"
                      className="h-4 w-4 cursor-help"
                    />
                    <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 w-64 p-2 bg-gray-800 text-white text-xs rounded shadow-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10">
                      Username must be 3-20 characters, lowercase letters, numbers, underscores, or hyphens. Must be unique.
                    </div>
                  </div>
                </span>
              </label>
              <div className="mt-1">
                <input
                  id="username"
                  type="text"
                  autoComplete="username"
                  className={`appearance-none block w-full px-3 py-2 border ${
                    errors.username || usernameError ? 'border-red-300' : 'border-gray-300'
                  } rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-green-500 focus:border-green-500 sm:text-sm lowercase`}
                  {...register('username', {
                    onChange: (e) => {
                      // Auto-convert to lowercase
                      e.target.value = e.target.value.toLowerCase();
                    },
                  })}
                  onBlur={handleUsernameBlur}
                />
                {checkingUsername && (
                  <p className="mt-2 text-sm text-gray-500">Checking availability...</p>
                )}
                {errors.username && (
                  <p className="mt-2 text-sm text-red-600">{errors.username.message}</p>
                )}
                {usernameError && !errors.username && (
                  <p className="mt-2 text-sm text-red-600">{usernameError}</p>
                )}
              </div>
            </div>
            
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                Email address
              </label>
              <div className="mt-1">
                <input
                  id="email"
                  type="email"
                  autoComplete="email"
                  className={`appearance-none block w-full px-3 py-2 border ${
                    errors.email || emailError ? 'border-red-300' : 'border-gray-300'
                  } rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-green-500 focus:border-green-500 sm:text-sm`}
                  {...register('email')}
                  onBlur={handleEmailBlur}
                />
                {checkingEmail && (
                  <p className="mt-2 text-sm text-gray-500">Checking availability...</p>
                )}
                {errors.email && (
                  <p className="mt-2 text-sm text-red-600">{errors.email.message}</p>
                )}
                {emailError && !errors.email && (
                  <p className="mt-2 text-sm text-red-600">
                    {emailError}
                  </p>
                )}
              </div>
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                Password
              </label>
              <div className="mt-1">
                <input
                  id="password"
                  type="password"
                  autoComplete="new-password"
                  className={`appearance-none block w-full px-3 py-2 border ${
                    errors.password ? 'border-red-300' : 'border-gray-300'
                  } rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-green-500 focus:border-green-500 sm:text-sm`}
                  {...register('password')}
                />
                {errors.password && (
                  <p className="mt-2 text-sm text-red-600">{errors.password.message}</p>
                )}
              </div>
            </div>

            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700">
                Confirm Password
              </label>
              <div className="mt-1">
                <input
                  id="confirmPassword"
                  type="password"
                  autoComplete="new-password"
                  className={`appearance-none block w-full px-3 py-2 border ${
                    errors.confirmPassword ? 'border-red-300' : 'border-gray-300'
                  } rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-green-500 focus:border-green-500 sm:text-sm`}
                  {...register('confirmPassword')}
                />
                {errors.confirmPassword && (
                  <p className="mt-2 text-sm text-red-600">{errors.confirmPassword.message}</p>
                )}
              </div>
            </div>

            <div>
              <div className="flex items-start">
                <div className="flex items-center h-5">
                  <input
                    id="termsAccepted"
                    type="checkbox"
                    className={`h-4 w-4 rounded border-gray-300 text-green-600 focus:ring-green-500 ${
                      errors.termsAccepted ? 'border-red-300' : ''
                    }`}
                    {...register('termsAccepted')}
                  />
                </div>
                <div className="ml-3 text-sm">
                  <label htmlFor="termsAccepted" className="text-gray-700">
                    I agree to the{' '}
                    <Link
                      to="/terms"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="font-medium text-green-600 hover:text-green-500 underline"
                    >
                      Terms and Conditions
                    </Link>
                    {' '}and{' '}
                    <Link
                      to="/privacy"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="font-medium text-green-600 hover:text-green-500 underline"
                    >
                      Privacy Policy
                    </Link>
                  </label>
                </div>
              </div>
              {errors.termsAccepted && (
                <p className="mt-2 text-sm text-red-600">{errors.termsAccepted.message}</p>
              )}
            </div>

            <div>
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50"
              >
                {isSubmitting ? 'Creating account...' : 'Create account'}
              </button>
            </div>
          </form>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default SignUpPage;
